import { cn } from "@/lib/utils";

/**
 * The Aviary logo (brand kit v3), as provided under `public/brand/`.
 *
 * - `horizontal` — small-cut mark + wordmark, for the expanded sidebar and the
 *   chat sidebar. 48px tall (≈141px wide); the kit's minimum is 140px wide.
 * - `mark` — the small-cut mark alone, for narrow spots (mobile top bar). Below
 *   96px the mark must be the small cut, never the one with "121" inside.
 * - `stacked` — mark, wordmark and tagline, for sign-in screens. 240px wide;
 *   never under 192px.
 *
 * "Positive" art (navy) is for the light theme, "reversed" (white, amber 121)
 * for dark; both are rendered and the theme class picks one, so there is no
 * flash when the stored theme is applied before first paint. The SVGs are
 * used as supplied: never recoloured, re-drawn or re-typeset.
 */
type Variant = "horizontal" | "mark" | "stacked";

const FILES: Record<Variant, string> = {
  horizontal: "aviary-horizontal-notagline-smallcut",
  mark: "aviary-mark-smallcut",
  stacked: "aviary-stacked-tagline",
};

const SIZE: Record<Variant, string> = {
  horizontal: "h-12 w-auto",
  mark: "h-8 w-auto",
  stacked: "w-[240px] h-auto",
};

export function Brand({
  className,
  variant = "horizontal",
}: {
  className?: string;
  variant?: Variant;
}) {
  const file = FILES[variant];
  const size = SIZE[variant];
  return (
    <span className={cn("inline-flex shrink-0 items-center", className)}>
      <img
        src={`/brand/${file}-positive.svg?v=3`}
        alt="The Aviary"
        className={cn(size, "dark:hidden")}
      />
      <img
        src={`/brand/${file}-reversed.svg?v=3`}
        alt="The Aviary"
        className={cn(size, "hidden dark:block")}
      />
    </span>
  );
}
